import { Dispatch, SetStateAction, useEffect, useState } from 'react'
import { useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import moment from 'moment'

// local imports
import loadingCalendar from 'assets/icons/loading-calendar.png'
import successImage from 'assets/images/success-image.svg'
import {
  ICalendarCoiffeurSearchForm,
  ICoiffeurSaloonSpecsFilteredList,
  IFilteredCoiffeurList,
} from 'types/CoiffeurType'
import { ISideBarModel } from 'types/CalendarType'
import CoiffeurService from 'service/CoiffeurService'
import AuthUtil from 'utils/AuthUtil'
import FormModal from 'components/ui/modal/FormModal'
import Loader from 'components/ui/Loader'
import NotificationDialog from 'components/ui/NotificationDialog'
import useCoiffeurReservationModal from 'components/home/on-campus-services/coiffeur/hooks/use-reservation-modal'
import { SearchModalHeader } from 'components/home/on-campus-services/coiffeur/coiffeur-search-modal/styled'
import useGetCoiffeurSalonList from 'components/home/on-campus-services/coiffeur/hooks/use-get-coiffeur-salon-list'
import useGetSalonLocationList from 'components/home/on-campus-services/coiffeur/hooks/use-get-salon-locations'
import CalendarCoiffeurReservationModal from './CalendarCoiffeurReservationModal'
import CalendarCoiffeurSearchForm from './CalendarCoiffeurSearchForm'

interface ICoiffeurSearchModalProps {
  modal: boolean
  setModal: Dispatch<SetStateAction<boolean>>
  closeCoiffeurModal: () => void
  selectedDay: ISideBarModel | undefined
  resetAll: Dispatch<SetStateAction<boolean>>
  sidebarAfterSave: () => void
}

export default function CalendarCoiffeurSearchModal({
  modal,
  setModal,
  selectedDay,
  resetAll,
  closeCoiffeurModal,
  sidebarAfterSave,
}: ICoiffeurSearchModalProps) {
  const { t } = useTranslation()
  const [loading, setLoading] = useState(false)
  const [coiffeurList, setCoiffeurList] = useState<IFilteredCoiffeurList[] | []>([])
  const [saloonSpecs, setSaloonSpecs] = useState<ICoiffeurSaloonSpecsFilteredList[] | []>([])
  const selectedDayDate = moment(selectedDay?.day.date).toDate()
  const selectedDateStr = moment(selectedDayDate).format('DD.MM.YYYY')

  /* Get Current logged employee infos - (location id) */
  let employeeLocation: number = 0
  const currentEmployee = AuthUtil.getEmployeeSummary()
  if (currentEmployee) employeeLocation = currentEmployee.locationId

  /* Form  */
  /* Filter form default values */
  const defaultValues: ICalendarCoiffeurSearchForm = {
    locationId: employeeLocation,
    coiffeurSalonId: 0,
    mainServiceId: 0,
    subServiceId: 0,
    coiffeur: null,
    reservationDate: selectedDayDate,
  }
  const filteredDate = defaultValues.reservationDate

  /* Search form state  */
  const { watch, resetField, control } = useForm<ICalendarCoiffeurSearchForm>({
    defaultValues,
  })
  const locationId = watch('locationId')
  const coiffeurSalonId = watch('coiffeurSalonId')
  const mainServiceId = watch('mainServiceId')
  const subServiceId = watch('subServiceId')
  const _selectedCoiffeur = watch('coiffeur')
  const reservationDate = watch('reservationDate')

  /* Get Coiffeur Salon List Get Coiffeur Salon Specialties - Locations | Custom Hooks */
  const { data: locationList } = useGetSalonLocationList()
  const { getSalonList, coiffeurSalons } = useGetCoiffeurSalonList()
  const selectedSalon = coiffeurSalons.find((s) => s.id === coiffeurSalonId)

  /* Get Coiffeur Saloon Specs (Gender related - subServices) */
  useEffect(() => {
    setLoading(true)
    CoiffeurService.getCoiffeurSaloonSpecs({
      coiffeurSaloonId: coiffeurSalonId,
      isOnlyUserGender: true,
    })
      .then((res) => setSaloonSpecs(res.data))
      .catch((error) => console.log(error))
      .finally(() => setLoading(false))
  }, [coiffeurSalonId])

  /* GET Coiffeur Salon List */
  useEffect(() => {
    getSalonList({ locationId: locationId, reservationDate: reservationDate })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [locationId])

  /* Get Coiffeurs - Coiffeur Workers List */
  useEffect(() => {
    if (!!coiffeurSalonId && !!subServiceId) {
      setLoading(true)
      CoiffeurService.getFilteredCoiffeurList({
        coiffeurSalloonId: coiffeurSalonId,
        coiffeurMainServiceId: mainServiceId,
        coiffeurSubServiceId: subServiceId,
        isIncludeNotActives: false,
      })
        .then((r) => setCoiffeurList(r.data))
        .catch((err) => console.log(err))
        .finally(() => setLoading(false))
    }
  }, [coiffeurSalonId, mainServiceId, subServiceId])

  /* ################## Reservation Modal ##################### */
  /* Reservation Modal (Open - Close Modal, Selected Coiffeur). | Custom Hook */
  const {
    openReservationModal,
    closeReservationModal,
    reservationModal,
    selectedCoiffeur,
    setSelectedCoiffeur,
    notification,
    setNotification,
  } = useCoiffeurReservationModal()

  /* Search Modal Methods */
  const handleOnCancel = () => closeCoiffeurModal()
  const selectCoiffeurAndOpen = () => {
    if (!!_selectedCoiffeur) {
      openReservationModal(_selectedCoiffeur)
      setModal(false)
    }
  }

  /* Appointment Scheduler Modal */
  const handleOnCancelAppointmentModal = () => {
    closeReservationModal()
    setModal(true)
  }

  /* Notification Modal */
  const handleCloseNotification = () => {
    setModal(false)
    setNotification(false)
    resetAll(false)
  }

  return (
    <>
      <FormModal
        id="coiffeurSelectionModal"
        closable
        header={<span>{t('dialog-texts.coiffeur-reservation-header')} (1/2)</span>}
        disabled={!_selectedCoiffeur}
        onSave={selectCoiffeurAndOpen}
        visible={modal}
        onHide={handleOnCancel}
        onCancel={handleOnCancel}>
        <SearchModalHeader style={{ marginBottom: '8px' }}>
          <div className="dialog-subheader">
            <span>{t('calendar.selected-day-info-text', { date: selectedDateStr })}</span>
          </div>
        </SearchModalHeader>

        <CalendarCoiffeurSearchForm
          watch={watch}
          control={control}
          resetField={resetField}
          coiffeurSalonList={coiffeurSalons}
          locationList={locationList}
          salonSpecs={saloonSpecs}
          coiffeurList={coiffeurList}
        />

        {loading ? (
          <div className="my-3 w-100">
            <Loader src={loadingCalendar} />
          </div>
        ) : null}
      </FormModal>

      {/* Reservation Timetable Modal */}
      {selectedCoiffeur ? (
        <CalendarCoiffeurReservationModal
          salon={selectedSalon}
          filterDate={filteredDate}
          subServiceId={subServiceId}
          locationId={locationId}
          coiffeurs={coiffeurList}
          open={reservationModal}
          cancelReservationModal={handleOnCancelAppointmentModal}
          openReservationModal={openReservationModal}
          closeReservationModal={closeReservationModal}
          showNotification={() => {
            setNotification(true)
            sidebarAfterSave()
          }}
          setSelectedCoiffeur={setSelectedCoiffeur}
          selectedCoiffeur={selectedCoiffeur}
          selectedDateStr={selectedDateStr}
        />
      ) : null}

      <NotificationDialog
        display={notification}
        imgSrc={successImage}
        goHomeButton={true}
        title={t('success-text.appointment-completed')}
        content={t('coiffeur-res-modal.notification-text')}
        firstButtonLabel={t('buttons.back-to-calendar')}
        onHide={handleCloseNotification}
        onSubmit={handleCloseNotification}
        selectedType={null}
        setSelectedType={undefined}
        types={[]}
      />
    </>
  )
}
